import { Survey } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo } from "react";
import { hslToHex } from "../../helpers/colors";
import { arrayShuffleInPlace } from "../../helpers/utils";
import { ResultProgressBar } from "@kalyzee/escaet-ui";

export interface SurveyResultsViewerColors {
  background: string;
  fill: string;
  text: string;
}

const COLORS: SurveyResultsViewerColors[] = Array.from({ length: 18 }).map((_, idx) => {
  const hue = idx * 20; // Generates hues from 0 to 340 in steps of 20
  return {
    background: hslToHex(hue, 1, 0.95),
    fill: hslToHex(hue, 1, 0.8),
    text: hslToHex(hue, 0.25, 0.5),
  };
});

const STARS_COLORS: SurveyResultsViewerColors = {
  ...COLORS[3],
  text: "gold",
};

export interface SurveyResultsViewerProps {
  survey?: Survey;
  customColors?: {
    normal?: SurveyResultsViewerColors[],
    stars?: SurveyResultsViewerColors,
  };
}

export const SurveyResultsViewer = ({
  survey,
  customColors,
}: SurveyResultsViewerProps) => {

  const data = useMemo(() => {
    if (!survey?.questions) return undefined;
    const question = survey.questions[0];
    if (!question) return undefined; // Only one question supported for now

    const questionVoteNb = question.choices.reduce((acc, val) => {
      if (val.votesNumber) return acc + val.votesNumber;
      return acc;
    }, 0);

    let randomColors: SurveyResultsViewerColors[] | undefined;
    let idx = 0;
    const getRandomColor = () => {
      if (!randomColors || idx === randomColors.length) {
        randomColors = [...(customColors?.normal || COLORS)];
        arrayShuffleInPlace(randomColors);
        idx = 0;
      }
      const color = randomColors[idx];
      idx++;
      return color;
    }

    const isStar = survey.name === "Étoiles";
    return {
      totalVotes: questionVoteNb,
      choices: question.choices.map(choice => {
        const colors = isStar ? (customColors?.stars ?? STARS_COLORS) : getRandomColor();
        return {
          id: choice.id,
          content: choice.content,
          votesNumber: choice.votesNumber ?? 0,
          isStar,
          colors,
        };
      })
    };
  }, [survey]);

  const choiceNb = data?.choices?.length || 0;
  const verticalMode = choiceNb > 5;
  const starMode = survey?.name === "Étoiles";
  return (
    <div style={{
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
    }}>
      <div 
        style={{
          height: "100%",
          display: "flex",
          overflowX: "auto",
          gap: "1.25rem",
          padding: "1rem",
          width: verticalMode ? "min(1000px, 90%)" : undefined,
          // alignSelf: verticalMode ? "center" : undefined,
          flexDirection: verticalMode ? "column" : "row",
          //justifyContent: "center",
        }}
      >
        {data?.choices.map((choice, index) =>
          <div style={{
            height: verticalMode ? "100px" : "100%",
            width: verticalMode ? "100%" : "600px",
          }}>
            <ResultProgressBar
              key={index}
              title={
                starMode 
                  ? { mode: "stars", value: parseFloat(choice.content) } 
                  : { mode: "text", value: choice.content }
              }
              total={data.totalVotes}
              number={choice.votesNumber}
              colors={choice.colors}
              orientation={verticalMode ? "horizontal" : "vertical"}
            />
          </div>
        )}
      </div>
    </div>
  );
};